import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import toast from 'react-hot-toast';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';

interface DefaultQuestion {
  id: string;
  question: string;
  responseType: 'text' | 'choice';
  required: boolean;
  order: number;
  choices: string[];
  defaultQuestion: boolean;
  active: boolean;
}

const DEFAULT_CHOICES = ['Excellent', 'Good', 'Neutral', 'Poor', 'Very Poor'];

const DefaultQuestionsManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<DefaultQuestion | null>(null);
  const [formData, setFormData] = useState<Partial<DefaultQuestion>>({
    question: '',
    responseType: 'choice',
    required: true,
    order: 0,
    choices: [],
    active: true
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<DefaultQuestion | null>(null);

  // Fetch default questions
  const { data: questionsResponse, isLoading } = useQuery({
    queryKey: ['default-questions'],
    queryFn: async () => {
      const response = await api.get('/default-questions');
      return response.data;
    }
  });

  const questions: DefaultQuestion[] = questionsResponse?.data || [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: Partial<DefaultQuestion>) => {
      const response = await api.post('/default-questions', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['default-questions'] });
      toast.success('Default question created successfully');
      setShowModal(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create default question');
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<DefaultQuestion> }) => {
      const response = await api.put(`/default-questions/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['default-questions'] });
      toast.success('Default question updated successfully');
      setShowModal(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update default question');
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/default-questions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['default-questions'] });
      toast.success('Default question deleted successfully');
      setShowDeleteModal(false);
      setQuestionToDelete(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete default question');
      // Keep modal open on error so user can retry or cancel
    }
  });

  const resetForm = () => {
    setFormData({
      question: '',
      responseType: 'choice',
      required: true,
      order: 0,
      choices: [],
      active: true
    });
    setEditingQuestion(null);
  };

  const handleCreate = () => {
    resetForm();
    // Set default choices for new 'choice' type questions
    setFormData(prev => ({
      ...prev,
      responseType: 'choice',
      choices: [...DEFAULT_CHOICES]
    }));
    setShowModal(true);
  };

  const handleEdit = (question: DefaultQuestion) => {
    setEditingQuestion(question);
    setFormData({
      question: question.question,
      responseType: question.responseType,
      required: question.required,
      order: question.order,
      choices: question.responseType === 'choice' 
        ? (question.choices && question.choices.length > 0 ? question.choices : [...DEFAULT_CHOICES])
        : [],
      active: question.active
    });
    setShowModal(true);
  };

  const handleDelete = (question: DefaultQuestion) => {
    setQuestionToDelete(question);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (!questionToDelete) return;
    deleteMutation.mutate(questionToDelete.id);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setQuestionToDelete(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.question?.trim()) {
      toast.error('Question text is required');
      return;
    }
    if (formData.responseType === 'choice' && (!formData.choices || formData.choices.length === 0)) {
      toast.error('At least one choice is required for single choice response type');
      return;
    }

    // Check if order is already taken by another ACTIVE question
    const orderValue = formData.order || 0;
    const existingQuestionWithOrder = questions.find(
      q => q.order === orderValue && 
           q.active === true && 
           (!editingQuestion || q.id !== editingQuestion.id)
    );
    if (existingQuestionWithOrder) {
      toast.error(`Order ${orderValue} is already taken by another active question. Please choose a different order.`);
      return;
    }

    if (editingQuestion) {
      updateMutation.mutate({ id: editingQuestion.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  // Auto-populate default choices when response type is 'choice'
  useEffect(() => {
    if (formData.responseType === 'choice' && (!formData.choices || formData.choices.length === 0)) {
      setFormData(prev => ({
        ...prev,
        choices: [...DEFAULT_CHOICES]
      }));
    } else if (formData.responseType === 'text') {
      setFormData(prev => ({
        ...prev,
        choices: []
      }));
    }
  }, [formData.responseType]);

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Default Questions</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage default questions that are automatically added to all survey templates (only active questions are added)
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Default Question
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {questions.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              No default questions found. Create your first default question.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Question
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Response Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Choices
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {questions.map((question) => (
                    <tr key={question.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {question.order}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{question.question}</div>
                        {question.required && (
                          <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                            Required
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          {question.responseType === 'choice' ? 'Single Choice' : 'Text'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {question.responseType === 'choice' && question.choices && question.choices.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {question.choices.map((choice, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800"
                              >
                                {choice}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {question.active ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(question)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(question)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Transition appear show={showModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => { setShowModal(false); resetForm(); }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="px-4 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <Dialog.Title
                        as="h3"
                        className="text-xl font-semibold text-gray-900"
                      >
                        {editingQuestion ? 'Edit Default Question' : 'Create Default Question'}
                      </Dialog.Title>
                      <button
                        onClick={() => { setShowModal(false); resetForm(); }}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {editingQuestion
                        ? 'Update the default question details below'
                        : 'Add a new active default question that is auto-included in all survey templates.'}
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
                    {/* Question Text */}
                    <div className="grid grid-cols-12 gap-6">
                      <div className="col-span-12">
                        <div className="grid grid-cols-12 gap-4 items-start">
                          <div className="col-span-3">
                            <label htmlFor="question" className="block text-left text-sm font-medium text-gray-700 mb-1 pt-2">
                              Question <span className="text-red-500">*</span>
                            </label>
                          </div>
                          <div className="col-span-9">
                            <textarea
                              id="question"
                              value={formData.question || ''}
                              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                              placeholder="Enter the question text"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Response Type */}
                    <div className="grid grid-cols-12 gap-6">
                      <div className="col-span-6">
                        <div className="grid grid-cols-12 gap-4 items-end">
                          <div className="col-span-6">
                            <label htmlFor="responseType" className="block text-left text-sm font-medium text-gray-700 mb-1">
                              Response Type <span className="text-red-500">*</span>
                            </label>
                          </div>
                          <div className="col-span-6">
                            <select
                              id="responseType"
                              value={formData.responseType || 'choice'}
                              onChange={(e) => setFormData({ ...formData, responseType: e.target.value as 'text' | 'choice' })}
                              className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              required
                            >
                              <option value="choice">Single Choice</option>
                              <option value="text">Text</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-6">
                        <div className="grid grid-cols-12 gap-4 items-start">
                          <div className="col-span-6">
                            <label htmlFor="order" className="block text-left text-sm font-medium text-gray-700 mb-1">
                              Order
                            </label>
                          </div>
                          <div className="col-span-6">
                            {(() => {
                              const orderValue = formData.order || 0;
                              // Only check for conflicts with ACTIVE questions
                              const existingQuestionWithOrder = questions.find(
                                q => q.order === orderValue && 
                                     q.active === true && 
                                     (!editingQuestion || q.id !== editingQuestion.id)
                              );
                              const hasError = !!existingQuestionWithOrder;
                              return (
                                <>
                                  <input
                                    id="order"
                                    type="number"
                                    value={formData.order || 0}
                                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                                    className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 ${
                                      hasError 
                                        ? 'border-red-300 focus:ring-red-400' 
                                        : 'border-gray-300 focus:ring-blue-400'
                                    }`}
                                    min="0"
                                    placeholder="0"
                                  />
                                  {hasError && (
                                    <p className="mt-1 text-xs text-red-600">Order {orderValue} is already taken by an active question</p>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Choices (read-only badges) */}
                    {formData.responseType === 'choice' && (
                      <div className="grid grid-cols-12 gap-6">
                        <div className="col-span-12">
                          <div className="grid grid-cols-12 gap-4 items-start">
                            <div className="col-span-3">
                              <label className="block text-left text-sm font-medium text-gray-700 mb-1 pt-2">
                                Choices <span className="text-red-500">*</span>
                              </label>
                            </div>
                            <div className="col-span-9">
                              {formData.choices && formData.choices.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {formData.choices.map((choice, idx) => (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800"
                                    >
                                      {choice}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500">No choices available</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Order, Required, and Active */}
                    <div className="grid grid-cols-12 gap-6">
                      
                      <div className="col-span-6">
                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="required"
                              type="checkbox"
                              checked={formData.required || false}
                              onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                          </div>
                          <div className="ml-3 text-xs">
                            <label htmlFor="required" className="font-medium text-gray-700">
                              Required
                            </label>
                            <p className="text-gray-500 text-xs">Question must be answered</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-6">
                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="active"
                              type="checkbox"
                              checked={formData.active || false}
                              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                          </div>
                          <div className="ml-3 text-xs">
                            <label htmlFor="active" className="font-medium text-gray-700">
                              Active
                            </label>
                            <p className="text-gray-500 text-xs">Only active questions are added to templates</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Form Actions */}
                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => { setShowModal(false); resetForm(); }}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={createMutation.isPending || updateMutation.isPending}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {createMutation.isPending || updateMutation.isPending
                          ? 'Saving...'
                          : editingQuestion
                          ? 'Update'
                          : 'Create'}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Delete Confirmation Modal */}
      {questionToDelete && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Delete Default Question"
          itemName={''}
          message={`Are you sure you want to delete the default question "${questionToDelete.question}"? This action cannot be undone.`}
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

export default DefaultQuestionsManagement;

